import { z } from "zod";
import { QueryParams } from "../../httpClient";
import {
    ProjectStatusEnum,
    QuoteStatusEnum,
    QuoteIntervalEnum,
    QuotePaymentModeEnum,
    QuotePaymentStatusEnum,
    ProjectParticipantGroupEnum,
    ProjectParticipantRoleEnum,
    ProjectCurrencyCodeSchema,
} from "./common";

export interface ProjectsV2ListQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    status?: z.infer<typeof ProjectStatusEnum>[];
}

export interface AdminProjectsV2ListQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    project_id?: number;
    lead_id?: number;
    project_title?: string;
    status?: z.infer<typeof ProjectStatusEnum>;
    expert_id?: number;
    client_id?: number;
}

export const UpdateProjectStatusRequestSchema = z.object({
    acting_org_id: z.number().int().positive(),
    project_status: ProjectStatusEnum,
    rejection_reason: z.string().optional(),
    auto_review_requests_enabled: z.boolean().optional(),
});
export type UpdateProjectStatusRequest = z.infer<typeof UpdateProjectStatusRequestSchema>;

export const AdminUpdateProjectStatusRequestSchema = z.object({
    project_status: ProjectStatusEnum,
});
export type AdminUpdateProjectStatusRequest = z.infer<typeof AdminUpdateProjectStatusRequestSchema>;

export const CreateProjectQuoteRequestSchema = z.object({
    acting_org_id: z.number().int().positive(),
    amount_cents: z.number().int().nonnegative(),
    currency: ProjectCurrencyCodeSchema.optional(),
    interval: QuoteIntervalEnum,
    quantity: z.number().int().positive(),
    delivery_after_days: z.number().int().nonnegative().nullable().optional(),
    includes_weekends: z.boolean().nullable().optional(),
    hours_bank_expires_after_days: z.number().int().nonnegative().nullable().optional(),
    hours_bank_expiration_terms: z.string().nullable().optional(),
    expires_at: z.string().datetime().nullable().optional(),
    payment_status: QuotePaymentStatusEnum.nullable().optional(),
    payment_mode: QuotePaymentModeEnum.nullable().optional(),
    external_payment_url: z.string().url().nullable().optional(),
    scope: z.string().nullable().optional(),
});
export type CreateProjectQuoteRequest = z.infer<typeof CreateProjectQuoteRequestSchema>;

export const UpdateProjectQuoteRequestSchema = z.object({
    acting_org_id: z.number().int().positive(),
    amount_cents: z.number().int().nonnegative().optional(),
    currency: ProjectCurrencyCodeSchema.optional(),
    interval: QuoteIntervalEnum.optional(),
    quantity: z.number().int().positive().optional(),
    delivery_after_days: z.number().int().nonnegative().nullable().optional(),
    includes_weekends: z.boolean().nullable().optional(),
    hours_bank_expires_after_days: z.number().int().nonnegative().nullable().optional(),
    hours_bank_expiration_terms: z.string().nullable().optional(),
    expires_at: z.string().datetime().nullable().optional(),
    payment_mode: QuotePaymentModeEnum.nullable().optional(),
    external_payment_url: z.string().url().nullable().optional(),
    scope: z.string().nullable().optional(),
});
export type UpdateProjectQuoteRequest = z.infer<typeof UpdateProjectQuoteRequestSchema>;

export const ProjectActingOrgRequestSchema = z.object({
    acting_org_id: z.number().int().positive(),
});
export type ProjectActingOrgRequest = z.infer<typeof ProjectActingOrgRequestSchema>;

export const UpdateProjectQuotePaymentStatusRequestSchema = z.object({
    acting_org_id: z.number().int().positive(),
    payment_status: QuotePaymentStatusEnum.nullable().optional(),
});
export type UpdateProjectQuotePaymentStatusRequest = z.infer<typeof UpdateProjectQuotePaymentStatusRequestSchema>;

export const AdminUpdateProjectQuoteStatusRequestSchema = z.object({
    status: QuoteStatusEnum,
});
export type AdminUpdateProjectQuoteStatusRequest = z.infer<typeof AdminUpdateProjectQuoteStatusRequestSchema>;

export const AdminUpdateProjectQuotePaymentStatusRequestSchema = z.object({
    payment_status: QuotePaymentStatusEnum.nullable().optional(),
});
export type AdminUpdateProjectQuotePaymentStatusRequest = z.infer<typeof AdminUpdateProjectQuotePaymentStatusRequestSchema>;

export const AdminStoreProjectParticipantRequestSchema = z.object({
    org_id: z.number().int().positive(),
    participant_group: ProjectParticipantGroupEnum,
    participant_role: ProjectParticipantRoleEnum,
    expires_at: z.string().datetime().nullable().optional(),
});
export type AdminStoreProjectParticipantRequest = z.infer<typeof AdminStoreProjectParticipantRequestSchema>;

export interface UploadProjectAttachmentsParams {
    acting_org_id: number;
    files: File[];
    metadata?: Record<string, unknown> | null;
}
